import {
    createStyles,
    FormHelperText as MuiFormHelperText,
    Input as MuiInput,
    makeStyles,
    TextFieldProps as MuiTextFieldProps,
} from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";

import { Label } from "../label";
import { RelodeTheme } from "../styles";
import {
    UnusedAriaProps,
    UnusedMuiEvents,
    UnusedMuiStandardProps,
} from "../types";
import { camelize } from "../utils";
import { useInputCommonStyles } from "./inputStyles";

const MAX_CHARACTER_WARNING = 10;

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            muiFormControl: {
                margin: 0,
            },
            characterLimit: {
                fontSize: 12,
                color: theme.relode.colors.backgroundText.main,
                marginTop: 3,
            },
            characterLimitNear: {
                color: theme.relode.colors.backgroundText.dark,
                fontWeight: "bold",
            },
            noError: {},
            subtext: {
                display: "grid",
                gridTemplateColumns: "1fr max-content",
            },
            noSubtext: {
                display: "none",
            },
        }),
    { name: "TextInput" }
);

export type TextInputProps = {
    showCharacterLimit?: boolean;
    maxCharacters?: number;
    label: string;
    compress?: boolean;
    value?: string | number;
    type?: "tel" | "email" | "date";
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    inputClassName?: string;
    disableFullWidth?: boolean;
    wrapContent?: boolean;
    onEnterKey?: (
        value: string | number | undefined,
        event?: React.KeyboardEvent<HTMLDivElement>
    ) => void;
} & Omit<
    MuiTextFieldProps,
    UnusedAriaProps | UnusedMuiEvents | UnusedMuiStandardProps | "value" // TODO remove and fix everywhere it breaks | "className"
> &
    Pick<
        MuiTextFieldProps,
        | "onChange"
        | "onBlur"
        | "onKeyPress"
        | "inputRef"
        | "placeholder"
        | "helperText"
        | "error"
        | "autoFocus"
    >;

export const TextInput = React.forwardRef(
    (props: TextInputProps, ref?: any) => {
        const {
            id,
            value,
            compress,
            showCharacterLimit,
            maxCharacters,
            label,
            error,
            helperText,
            fullWidth,
            disableFullWidth,
            onChange,
            onKeyPress,
            onBlur,
            inputRef,
            onEnterKey,
            className,
            inputClassName,
            margin = "none",
            InputLabelProps,
            InputProps,
            wrapContent,
            ...passthrough
        } = props;

        const classes = useStyles({});
        const commonClasses = useInputCommonStyles({});

        const dataCy = props["data-cy"] ?? camelize(props.id ?? "");
        const muiInputClasses = classnames(commonClasses.inputRoot, {
            [classes.noError]: !props.error,
            [commonClasses.wrappedInput]: wrapContent,
        });

        const {
            characterLimitMessage,
            remainingCharacters,
        } = React.useMemo(() => {
            if (!showCharacterLimit)
                return {
                    characterLimitMessage: undefined,
                    remainingCharacters: undefined,
                };

            if (typeof value === "string") {
                return {
                    characterLimitMessage: `${value.length} / ${maxCharacters}`,
                    remainingCharacters: maxCharacters - value.length,
                };
            }
        }, [showCharacterLimit, value, maxCharacters]);

        const characterLimitClasses = classnames({
            [classes.characterLimit]: showCharacterLimit,
            [classes.characterLimitNear]:
                showCharacterLimit &&
                remainingCharacters >= 0 &&
                remainingCharacters < MAX_CHARACTER_WARNING,
        });

        const subTextClasses = classnames(classes.subtext, {
            [classes.noSubtext]:
                !showCharacterLimit &&
                compress &&
                (!helperText || helperText === ""),
        });

        const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" && !!onEnterKey) {
                onEnterKey(value, event);
            }
            onKeyPress?.(event);
        };

        const handleChange = (
            e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
        ) => {
            if (maxCharacters > 0 && e.target.value.length > maxCharacters)
                return;
            onChange?.(e);
        };

        return (
            <Label
                data-cy={`${dataCy}-label`}
                label={label}
                fullWidth={!disableFullWidth}
                forInput
                className={className}
                error={error}
            >
                <>
                    <MuiInput
                        {...passthrough}
                        {...InputProps}
                        ref={ref}
                        error={error}
                        onChange={handleChange}
                        onBlur={onBlur}
                        onKeyPress={handleKeyPress}
                        value={value}
                        className={muiInputClasses}
                        inputProps={{
                            ...props.inputProps,
                            "data-cy": `${dataCy}-input`,
                            className: classnames(
                                commonClasses.innerInput,
                                inputClassName
                            ),
                        }}
                    />
                    <div className={subTextClasses}>
                        <MuiFormHelperText
                            error={props.error}
                            data-cy={`${dataCy}-helperText`}
                        >
                            {!!helperText ? helperText : " "}
                        </MuiFormHelperText>
                        <div className={characterLimitClasses}>
                            {characterLimitMessage}
                        </div>
                    </div>
                </>
            </Label>
        );
    }
);
